/* eslint-disable unicorn/numeric-separators-style */
import {
  ContractStatus,
  type CreateEmployeeContractResponse,
  type DepositType,
  type Employee,
  type EmployeeContract,
  type EmployeeHasSupplementaryBenefitsType,
  EmployeeType,
  employeesNamespace,
  type GetCompanyEmployeesAPIResponse,
  type GetEmployeeAPIResponse,
  type GetEmployeesSimplifiedAPIResponse,
  type LODFieldType,
  OnboardingStatusValues,
  type SimplifiedEmployee,
} from '@omnipresentgroup/types';

import { DEFAULT_COMPANY } from '../companies';
import { DEFAULT_EMPLOYMENT } from '../employments';
import { DEFAULT_LEGAL_ENTITY } from '../legalEntities';

export const DEFAULT_EMPLOYEE_CONTRACT: EmployeeContract = {
  createdAt: '2022-10-28T11:04:00.473Z',
  avvokaDocumentId: '87580',
  contractId: '246b3631-a4d3-4ef4-99c8-c8940372eb6d',
  documentId: '2456ccf4-4dd5-429c-8a9e-81e71495d32f',
  version: 10,
  avvokaTemplateId: '50534',
  status: ContractStatus.SIGNED_BOTH_PARTIES,
  updatedAt: '2022-10-28T11:07:17.321Z',
};

export const generateEmployeeContract = (
  override: Partial<EmployeeContract> = {},
) => ({
  ...DEFAULT_EMPLOYEE_CONTRACT,
  ...override,
});

export const DEFAULT_EMPLOYEE: Employee = {
  currentEmploymentId: DEFAULT_EMPLOYMENT.id,
  name: 'Sofiane Pamar',
  userId: '2fd23bcf-d11e-4f6b-bba5-9e83db4a845d',
  managerId: DEFAULT_COMPANY.id,
  profilePicture:
    'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png',
  needsRequiredFieldsFilled: false,
  requireTypeFormFilled: false,
  onboardingFormResponses: [
    {
      typeformId: 'OduYgfVs',
      filledAt: '2022-04-26T14:39:31.640Z',
      typeformResponseId: 'hlv96qo62d4ex63hlv9797cpchhstc7z',
    },
  ],
  externalReferences: [],
  id: 'def2967b-f6fa-48a7-82f0-cf56ecd8b5a7',
  lspId: 'of1WbA9Hc2OP',
  legalEntityId: DEFAULT_LEGAL_ENTITY.id,
  externalId: 'SS1-22-9985815',
  country: {
    id: '',
    name: 'United Kingdom',
    currency: '',
  },
  contactDetails: {
    personalEmail: 'simon.godefroid+sofiane@omnipresent.com',
    phoneNumber: '4353453543',
    workEmail: '',
  },
  onboardingStatus: {
    status: OnboardingStatusValues.ACTIVE,
  },
  personalDetails: {
    salutation: '',
    firstName: 'Sofiane',
    lastName: 'Pamar',
    dateOfBirth: '2000-12-12',
    socialInsuranceNumber: {
      localName: 'Social insurance number (or equivalent)',
      number: 'qweqwe',
    },
    maritalStatus: '',
    noOfChildren: 0,
    privateAddress: {
      addressLine1: 'qwqeq@mail.com',
      city: 'Paris',
      postalCode: '75013',
      country: 'United Kingdom',
      addressLine2: '',
      addressLine3: '',
    },
    citizenship: 'France',
    bankingDetails: {
      country: '',
      accountHolderName: '',
      accountNumber: 'qweqwe',
      bankName: '',
      bicSwift: 'qweqwe',
      transitNumber: '',
      insituteNumber: '',
      sortCode: '',
      routingNumber: '',
      iban: '',
      branchName: '',
    },
    gender: 'Male',
  },
  employmentDetails: {
    locationOfWork: {
      type: 'Home office',
      address: {
        addressLine1: 'qweqwe',
        city: 'Paris',
        postalCode: '123123',
        country: '',
      },
    },
    jobTitle: 'Pianist',
    startDate: '2023-06-20',
    firstPayDate: '2022-11-01',
    jobDescription: 'Play piano',
    additionalCompensation: '',
    hoursOfWork: 40,
    rightToWorkCheckCompleted: true,
    probationaryPeriod: {
      length: 0,
      timeUnit: 'Months',
    },
    annualBaseSalary: {
      amount: 5.5,
      currency: 'GBP',
    },
    hasSupplementaryBenefits: 'unknown' as EmployeeHasSupplementaryBenefitsType,
    leave: [
      {
        type: 'holiday:yearly-allowance-generic',
        value: 30,
      },
    ],
    salaryHistory: [
      {
        reason: 'Annual raise',
        amount: 5.5,
        notes: '',
        requestedAt: '2023-07-18T12:47:24.385Z',
        requestedByAuthId: 'auth0|60900ca7bf3ad00070cd4634',
        jobTitle: 'Pianist',
        hoursOfWork: 40,
        currency: 'GBP',
        jobDescription: 'Play piano',
        effectiveDate: '2023-08-01',
      },
      {
        reason: 'Annual raise',
        amount: 100000001,
        notes: '',
        requestedAt: '2023-03-08T20:36:55.740Z',
        requestedByAuthId: 'auth0|60900ca7bf3ad00070cd4634',
        jobTitle: 'Pianist',
        hoursOfWork: 40,
        currency: 'GBP',
        jobDescription: 'Play piano',
        effectiveDate: '2023-04-01',
      },
    ],
  },
  employeeType: EmployeeType.EOR,
  createdAt: '2022-04-26T13:55:14.369Z',
  updatedAt: '2023-08-30T15:00:40.832Z',
  localOnboardingDetails: [
    {
      name: 'national-insurance-number',
      value: '123',
      label: 'National insurance number',
      type: 'text' as LODFieldType,
    },
    {
      name: 'has-eligibility-to-work-in-living-country',
      value: false,
      label: 'Has eligibility to work in the country in which you live?',
      type: 'boolean' as LODFieldType,
    },
    {
      name: 'type-of-work-permit',
      value: 'Passport/citizenship',
      label: 'Type of work permit',
      type: 'text' as LODFieldType,
    },
  ],
  contractQuestionnaire: {
    templateId: 50534,
    body: {
      durationNonCompete: {
        position: 21,
        title:
          'For how many months should the employee be restricted by the non-compete clause after the employment?',
        type: 'select_list',
        value: '1',
        hint: '',
      },
      durationNonSolicit: {
        position: 22,
        title:
          'For how many months should the employee be restricted by the non-solicit clause after the employment?',
        type: 'select_list',
        value: ' 2',
        hint: '',
      },
      noOfHolidays: {
        position: 12,
        title:
          'Please provide the number of days for annual leave excluding public holidays.',
        type: 'select_list',
        value: '20',
        hint: 'Statutory minimum holiday entitlement is 20 days excluding public holidays. Best practice is granting 25 - 28 excluding public holidays.',
      },
      jobTitle: {
        position: 6,
        title: 'Please provide the Job Title for the new employee.',
        type: 'text',
        value: 'New job title please blah blah',
        hint: '',
      },
      companyName: {
        position: 18,
        title: 'What is the name of your company?',
        type: 'text',
        value: 'Simon Says',
        hint: '',
      },
      yourIndustry: {
        position: 20,
        title: 'What industry are you working in?',
        type: 'text',
        value: 'Entertainment Industry',
        hint: '',
      },
      employeeAddress: {
        position: 1,
        title: 'Please provide employees address in the standard local format.',
        type: 'text',
        value: 'Somewhere fancy, 75013, Paris, United Kingdom',
        hint: '',
      },
      unlimitedAnnualLeaveYesNo: {
        position: 13,
        title: 'Are you offering unlimited annual leave?',
        type: 'radio_button',
        value: 'No',
        hint: '',
      },
      workingHoursStartTime: {
        position: 7,
        title: 'Please provide the average daily starting time.',
        type: 'number',
        value: '7',
        hint: 'Please enter the starting hour in the following format - 9:00 for 9 am/ 6:00 for 6pm.',
      },
      payableOvertimeYesNo: {
        position: 10,
        title: 'Will the allowed overtime be payable?',
        type: 'radio_button',
        value: 'No',
        hint: 'Statutory maximum weekly working hours including overtime are capped at 48. Employers do not have to pay overtime unless it is outlined in the contract. It is not market practice to pay for overtime, rather the total salary should generally reflect the overall working hours expectations. ',
      },
      additionalSickLeaveYesNo: {
        position: 14,
        title:
          'Are you offering additional sick leave at full pay above the statutory minimum?',
        type: 'radio_button',
        value: 'No',
        hint: 'In the UK the government requires employers to provide 28 weeks paid sick leave to their employees. The first 3 days of the leave are not required to be paid, but most employers will cover this at full pay. From day 3 to the end of the 28 week period the employee is entitled to £96.35 per week that they are off sick. This is paid by the employer and cannot be claimed back from the government. ',
      },
      probationPeriodYesNo: {
        position: 3,
        title: 'Will there be a probation period?',
        type: 'radio_button',
        value: 'No',
        hint: '',
      },
      noticePeriodLength: {
        position: 17,
        title: 'Please provide a number of week(s) for the notice period.',
        type: 'select_list',
        value: '1',
        hint: "Statutory minimum notice period depends on the duration of the current employment: 1 month - 2 years of service - 1 week's notice. Every additional year of service adds one additional week to the notice period. Market practice is 1-3 months notice period after probation.",
      },
      salaryAmount: {
        position: 11,
        title: 'Please provide base salary in respective currency.',
        type: 'number',
        value: '100000000',
        hint: 'Please just type in the number.',
      },
      fullLegalName: {
        position: 0,
        title: 'Please provide the FULL LEGAL NAME of the employee.',
        type: 'text',
        value: 'Will it create tons of contracts?',
        hint: '',
      },
      restrictiveCovenantsYesNo: {
        position: 19,
        title:
          'Should the employment contract include Restrictive Covenant Clauses (Non-compete, Non-solicit)?',
        type: 'radio_button',
        value: 'Yes',
        hint: '',
      },
      hoursOfWork: {
        position: 9,
        title: 'Please provide standard weekly working hours.',
        type: 'number',
        value: '45',
        hint: '',
      },
      workingHoursEndTime: {
        position: 8,
        title: 'Please provide average daily ending time.',
        type: 'number',
        value: '10',
        hint: 'Please enter the starting hour in the following format - 9:00 for 9 am/ 6:00 for 6pm.',
      },
      startDate: {
        position: 2,
        title: 'Please provide the employment starting date.',
        type: 'date',
        value: '2023-06-20',
        hint: 'Please pick a date.',
      },
    },
  },
  updatedByAuthId: 'auth0|60900ca7bf3ad00070cd4634',
  contracts: [
    DEFAULT_EMPLOYEE_CONTRACT,
    generateEmployeeContract({
      archivedAt: '2022-10-20T12:34:04.674Z',
      createdAt: '2022-06-07T13:41:18.555Z',
      avvokaDocumentId: '78188',
      contractId: '74300a6a-a573-4c3e-adda-3275969ac704',
      documentId: '88fc91ca-8634-4ab0-a3bd-987bace69148',
      version: 9,
      avvokaTemplateId: '50534',
      status: ContractStatus.ARCHIVED,
      updatedAt: '2022-06-07T13:41:18.555Z',
    }),
  ],
  newUpdatedBy: 'auth0|60900ca7bf3ad00070cd4634',
  newUpdatedAt: '2023-08-30T15:00:40.833Z',
  adminNotes: '',
  payrollDetails: {
    managementFee: {
      amount: '100',
      currency: 'EUR',
    },
    payrollId: '629605',
    setupFee: {
      amount: '100',
      currency: 'EUR',
    },
    billingCurrency: 'EUR',
    depositType: 'None' as DepositType,
    zeroDepositFee: {
      amount: '0',
      currency: 'EUR',
    },
    employeeType: EmployeeType.EOR,
    depositFee: {
      calculatedAmount: '0',
      convertedAmount: '0',
    },
    advancedPaymentDepositPercentage: 0,
    fxFee: {
      currency: 'EUR',
      amount: 0,
    },
    annualBaseSalary: {
      amount: 100000000,
      currency: 'GBP',
    },
  },
  employmentInformation:
    'harmless test [logically](https://en.wikipedia.org/wiki/List_of_fallacies) invalid with a link ',
  temporaryPassword: '9q6xAGP8qg%jDA',
  seenPrivacyPolicy: '2022-04-26T14:38:22.723Z',
  companyName: '',
};

export const generateEmployee = (override: Partial<Employee> = {}) => ({
  ...DEFAULT_EMPLOYEE,
  ...override,
});

export const DEFAULT_SIMPLIFIED_EMPLOYEE: SimplifiedEmployee = {
  id: DEFAULT_EMPLOYEE.id,
  userId: DEFAULT_EMPLOYEE.userId,
  name: 'Sofiane Pamar',
  email: 'simon.godefroid+sofiane@omnipresent.com',
  country: 'United Kingdom',
  currency: 'GBP',
  annualBaseSalary: 5.5,
  jobTitle: 'Pianist',
  startDate: '2023-06-20',
  onboardingStatus: OnboardingStatusValues.ACTIVE,
  managerId: DEFAULT_EMPLOYEE.managerId,
  lspId: 'of1WbA9Hc2OP',
  nextCutoffDate: '2025-06-15T23:59:00.000Z',
};

// TODO: create a simplifier method?
export const generateSimplifiedEmployee = (
  override: Partial<SimplifiedEmployee> = {},
) => ({
  ...DEFAULT_SIMPLIFIED_EMPLOYEE,
  ...override,
});

export const DEFAULT_GET_EMPLOYEES_SIMPLIFIED_RESPONSE: GetEmployeesSimplifiedAPIResponse =
  {
    [employeesNamespace]: [DEFAULT_SIMPLIFIED_EMPLOYEE],
  };

export const DEFAULT_GET_COMPANY_EMPLOYEES_RESPONSE: GetCompanyEmployeesAPIResponse =
  {
    employees: [DEFAULT_EMPLOYEE as GetEmployeeAPIResponse],
  };

export const CREATE_EMPLOYEE_CONTRACT_RESPONSE: CreateEmployeeContractResponse =
  {
    contractId: 'sample-contract-id',
  };
